import { AppAdminFormLabel, AppButton, AppForm, AppTextField } from '@/components/atoms';
import { townEditFormSchema, type TownEditFormData } from '@/schemas/townSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import React, { useEffect } from 'react';
import { Controller, type SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

export interface TownEditInitialData {
  name: string;
  kana: string;
  postal_code1: string;
  postal_code2: string;
  search_name: string;
}

type ApiFieldErrors = Partial<Record<keyof TownEditFormData, string>>;

interface EditTownFormProps {
  initialData: TownEditInitialData;
  onSubmit: (data: TownEditFormData) => Promise<void> | void;
  isLoading?: boolean;
  apiErrors?: ApiFieldErrors;
}

const EditTownForm: React.FC<EditTownFormProps> = ({
  initialData,
  onSubmit,
  isLoading = false,
  apiErrors = {},
}) => {
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setError,
    watch,
  } = useTrimmedForm<TownEditFormData>({
    resolver: zodResolver(townEditFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      name: initialData.name,
      kana: initialData.kana,
      postal_code1: initialData.postal_code1,
      postal_code2: initialData.postal_code2,
      search_name: initialData.search_name,
    },
  });

  useEffect(() => {
    for (const [field, message] of Object.entries(apiErrors)) {
      if (message) {
        setError(field as keyof TownEditFormData, { type: 'api', message });
      }
    }
  }, [apiErrors, setError]);

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (!name) return;
      clearErrors(name as keyof TownEditFormData);
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  const submit: SubmitHandler<TownEditFormData> = async data => {
    await onSubmit(data);
  };

  const renderField = (label: string, fieldName: keyof TownEditFormData, required = true) => {
    const msg = errors[fieldName]?.message;
    return (
      <div className="mb-4">
        <AppAdminFormLabel label={label} required={required} />
        <Controller
          name={fieldName}
          control={control}
          render={({ field }) => (
            <AppTextField {...field} status={msg ? 'error' : undefined} className="w-full" />
          )}
        />
        {msg && <p className="mt-1 text-sm text-red-600">{msg}</p>}
      </div>
    );
  };

  return (
    <AppForm
      onFinish={handleSubmit(submit)}
      layout="vertical"
    >
      {renderField('名前', 'name')}
      {renderField('名前カナ', 'kana')}
      {renderField('郵便番号1', 'postal_code1')}
      {renderField('郵便番号2', 'postal_code2')}
      {renderField('検索名', 'search_name')}

      <div className="flex gap-3 mt-6 justify-end">
        <AppButton type="primary" className="px-10" htmlType="submit" loading={isLoading}>
          更新
        </AppButton>
        <AppButton onClick={() => navigate(-1)}>前画面に戻る</AppButton>
      </div>
    </AppForm>
  );
};

export default EditTownForm;
